// ...existing code...
// Real-time API client for Maersk metrics integration with env=prod filters
import axios, { AxiosInstance } from 'axios'

const getApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  
  // Fallback to localhost for development if not set
  if (!envUrl) {
    console.warn('REACT_APP_API_BASE_URL not set, using localhost fallback');
    return 'http://localhost:8080';  // Updated to match Flask backend port
  }
  
  return envUrl;
};

export const API_BASE_URL = getApiBaseUrl();
// const API_BASE_URL = 'http://localhost:80'; // Updated to integrated Flask backend

interface AuthConfig {
  clientId: string
  clientSecret: string
  scope: string
  tokenUrl: string
}

interface ApiConfig {
  consumerKey: string
  metricsBaseUrl: string
  logsBaseUrl: string
}

interface PrometheusQuery {
  query: string
  time?: string
  timeout?: string
}

interface PrometheusRangeQuery extends PrometheusQuery {
  start: string
  end: string
  step: string
}

interface PrometheusResponse {
  status: string
  data: {
    resultType: string
    result: Array<{
      metric: Record<string, string>
      value?: [number, string]
      values?: Array<[number, string]>
    }>
  }
}

// Fix: Use ES2015 target for downlevelIteration
// If using tsconfig.json, ensure target is at least 'es2015'
// Example:
// "target": "es2015",
// "downlevelIteration": true,

// Centralized AKS metrics list for correlation and AI analysis
export const AKS_METRICS_LIST = [
  // Only include metrics relevant for anomaly correlation and recommendations
  // (Full list can be imported or trimmed as needed)
  "container_cpu_usage_seconds_total",
  "container_memory_working_set_bytes",
  "kube_pod_container_status_restarts_total",
  "kube_pod_container_status_terminated_reason",
  "kube_pod_container_status_waiting_reason",
  "kube_node_status_condition",
  "node_memory_MemAvailable_bytes",
  "node_memory_MemTotal_bytes",
  "node_cpu_seconds_total",
  "kubelet_volume_stats_used_bytes",
  "kubelet_volume_stats_capacity_bytes",
  "container_fs_reads_total",
  "container_fs_writes_total",
  "container_network_receive_errors_total",
  "container_network_transmit_errors_total",
  "apiserver_request_duration_seconds_bucket",
  // ...add more from your full list as needed...
];

class MaerskApiClient {
  private authConfig: AuthConfig
  private apiConfig: ApiConfig
  private axiosInstance: AxiosInstance
  private accessToken: string | null = null
  private tokenExpiry: number = 0

  constructor() {
    this.authConfig = {
      clientId: process.env.REACT_APP_CLIENT_ID || '2c1de1bc-6733-465f-bd6b-cc438523186f',
      clientSecret: process.env.REACT_APP_CLIENT_SECRET || '',
      scope: process.env.REACT_APP_SCOPE || 'api://ingestion.pensieve/.default',
      tokenUrl: process.env.REACT_APP_TOKEN_URL || 'https://login.microsoftonline.com/05d75c05-fa1a-42e7-9cf1-eb416c396f2d/oauth2/v2.0/token'
    };

    this.apiConfig = {
      consumerKey: process.env.REACT_APP_CONSUMER_KEY || 'DW2y7McULGkYrygqbk6zPxZtFUHnV10p',
      // Default to integrated Flask backend for development, can be overridden by env vars
      metricsBaseUrl: API_BASE_URL, // Always use integrated Flask backend
      logsBaseUrl: `${API_BASE_URL}/api/oracle-proxy/logs` // Use oracle proxy logs endpoint
    };    
    this.axiosInstance = axios.create({
      timeout: 600000, // 10 minutes timeout
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    // Add request interceptor for proxy vs direct API calls
    this.axiosInstance.interceptors.request.use(
      async (config: any) => {
        // Always use JWT + Consumer-Key for FastAPI backend
        await this.ensureValidToken();
        config.headers['Authorization'] = `Bearer ${this.accessToken}`;
        config.headers['ConsumerKey'] = this.apiConfig.consumerKey;
        return config;
      },
      (error: any) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        if (error.response?.status === 401) {
          this.accessToken = null;
          await this.ensureValidToken();
          return this.axiosInstance.request(error.config);
        }
        return Promise.reject(error);
      }
    );
  }

  private async getAccessToken(): Promise<string> {
    // Call integrated Flask app /api/oracle-proxy/auth/token to get JWT and consumer key
    const response = await axios.post(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/auth/token`);
    if (response.data.token && response.data.consumer_key) {
      this.accessToken = response.data.token || '';
      this.tokenExpiry = Date.now() + 3600 * 1000 - 60000; // 1 hour expiry minus 1 min
      this.apiConfig.consumerKey = response.data.consumer_key;
      return this.accessToken || '';
    } else {
      throw new Error('No token received from integrated Flask backend');
    }
  }

  private async ensureValidToken(): Promise<void> {
    if (!this.accessToken || Date.now() >= this.tokenExpiry) {
      await this.getAccessToken();
    }
  }

  // Prometheus query methods
  async queryInstant(query: string, time?: string): Promise<PrometheusResponse> {
    try {
      // Remove job parameter from query string
      const cleanedQuery = query.replace(/job=~?"[^"]*"[, ]*/g, '');
      const params: PrometheusQuery = { query: cleanedQuery };
      if (time) params.time = time;

      const response = await this.axiosInstance.get(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/v1/query`, {
        params
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to execute instant query:', error);
      throw new Error(`Query failed: ${error.response?.data?.error || error.message}`);
    }
  }

  async queryRange(query: string, start: string, end: string, step: string): Promise<PrometheusResponse> {
    try {
      // Remove job parameter from query string
      const cleanedQuery = query.replace(/job=~?"[^"]*"[, ]*/g, '');
      const params: PrometheusRangeQuery = { query: cleanedQuery, start, end, step };

      const response = await this.axiosInstance.get(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/v1/query_range`, {
        params
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to execute range query:', error);
      throw new Error(`Range query failed: ${error.response?.data?.error || error.message}`);
    }
  }

  // Cache methods for backend DB storage
  async getCache(cacheKey: string): Promise<any> {
    try {
      const response = await this.axiosInstance.get(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/cache/${cacheKey}`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to get cache:', error);
      throw error;
    }
  }

  async setCache(cacheKey: string, data: { value: string; expiry: number }): Promise<any> {
    try {
      const response = await this.axiosInstance.post(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/cache/${cacheKey}`, data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to set cache:', error);
      throw error;
    }
  }

  async deleteCache(cacheKey: string): Promise<any> {
    try {
      const response = await this.axiosInstance.delete(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/cache/${cacheKey}`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to delete cache:', error);
      throw error;
    }
  }

  // Get cache status for database-specific caches
  async getCacheStatus(dbType: string): Promise<any> {
    try {
      const response = await this.axiosInstance.get(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/cache/status/${dbType}`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to get cache status:', error);
      throw error;
    }
  }

  // Dashboard methods with detailed breakdowns for actionable insights
  async getClusterHealth(): Promise<any> {
    try {
      // Get detailed node status by cluster and condition
      const [nodesByCluster, nodeConditions, podsByNamespace] = await Promise.all([
        this.queryInstant('kube_node_info{env="prod"}'),
        this.queryInstant('kube_node_status_condition{env="prod"}'),
        this.queryInstant('kube_pod_info{env="prod"}')
      ]);

      // Process detailed node information
      const nodeDetails = nodesByCluster.data.result.map((node: any) => ({
        name: node.metric.node,
        cluster: node.metric.cluster || 'unknown',
        zone: node.metric.zone || 'unknown',
        instance_type: node.metric.instance_type || 'unknown',
        kernel_version: node.metric.kernel_version || 'unknown'
      }));

      // Process node conditions (Ready, DiskPressure, MemoryPressure, PIDPressure)
      const nodeConditionDetails = nodeConditions.data.result.map((condition: any) => ({
        node: condition.metric.node,
        cluster: condition.metric.cluster || 'unknown',
        condition: condition.metric.condition,
        status: condition.metric.status,
        timestamp: condition.value[0]
      }));

      // Process pod distribution by namespace and cluster
      const podDistribution = podsByNamespace.data.result.reduce((acc: any, pod: any) => {
        const cluster = pod.metric.cluster || 'unknown';
        const namespace = pod.metric.namespace || 'default';
        const key = `${cluster}/${namespace}`;
        
        if (!acc[key]) {
          acc[key] = { cluster, namespace, podCount: 0, pods: [] };
        }
        acc[key].podCount++;
        acc[key].pods.push({
          name: pod.metric.pod,
          node: pod.metric.node,
          phase: pod.metric.phase
        });
        return acc;
      }, {});

      return {
        nodeDetails,
        nodeConditionDetails,
        podDistribution: Object.values(podDistribution),
        summary: {
          totalNodes: nodeDetails.length,
          // Fix for Set iteration error: use Array.from instead of spread
          totalClusters: Array.from(new Set(nodeDetails.map((n: any) => n.cluster))).length,
          nodesReady: nodeConditionDetails.filter((c: any) => c.condition === 'Ready' && c.status === 'true').length,
          nodesWithIssues: nodeConditionDetails.filter((c: any) => c.condition !== 'Ready' && c.status === 'true').length
        }
      };
    } catch (error) {
      console.error('❌ Failed to get cluster health details:', error);
      throw error;
    }
  }

  async getPodHealthDetails(): Promise<any> {
    try {
      // Get detailed information about pod failures and issues
      const [oomKilledPods, crashLoopPods, pendingPods, failedPods] = await Promise.all([
        this.queryInstant('kube_pod_container_status_terminated_reason{reason="OOMKilled",env="prod"} == 1'),
        this.queryInstant('kube_pod_container_status_waiting_reason{reason="CrashLoopBackOff",env="prod"} == 1'),
        this.queryInstant('kube_pod_status_phase{phase="Pending",env="prod"} == 1'),
        this.queryInstant('kube_pod_status_phase{phase="Failed",env="prod"} == 1')
      ]);

      // Process OOM killed pods with details
      const oomDetails = oomKilledPods.data.result.map((pod: any) => ({
        podName: pod.metric.pod,
        namespace: pod.metric.namespace,
        cluster: pod.metric.cluster || 'unknown',
        node: pod.metric.node,
        container: pod.metric.container,
        timestamp: pod.value[0],
        severity: 'critical',
        action: 'Increase memory limits or investigate memory leaks',
        impact: 'Pod restart, potential service disruption'
      }));

      // Process crash loop pods with details
      const crashLoopDetails = crashLoopPods.data.result.map((pod: any) => ({
        podName: pod.metric.pod,
        namespace: pod.metric.namespace,
        cluster: pod.metric.cluster || 'unknown',
        node: pod.metric.node,
        container: pod.metric.container,
        timestamp: pod.value[0],
        severity: 'high',
        action: 'Check application logs and configuration',
        impact: 'Service unavailable, repeated restart cycles'
      }));

      // Process pending pods (scheduling issues)
      const pendingDetails = pendingPods.data.result.map((pod: any) => ({
        podName: pod.metric.pod,
        namespace: pod.metric.namespace,
        cluster: pod.metric.cluster || 'unknown',
        timestamp: pod.value[0],
        severity: 'medium',
        action: 'Check resource requests, node capacity, and scheduling constraints',
        impact: 'Service deployment delayed'
      }));

      // Process failed pods
      const failedDetails = failedPods.data.result.map((pod: any) => ({
        podName: pod.metric.pod,
        namespace: pod.metric.namespace,
        cluster: pod.metric.cluster || 'unknown',
        node: pod.metric.node,
        timestamp: pod.value[0],
        severity: 'high',
        action: 'Investigate pod logs and events',
        impact: 'Service failure'
      }));

      return {
        oomKilledPods: oomDetails,
        crashLoopPods: crashLoopDetails,
        pendingPods: pendingDetails,
        failedPods: failedDetails,
        summary: {
          totalIssues: oomDetails.length + crashLoopDetails.length + pendingDetails.length + failedDetails.length,
          criticalIssues: oomDetails.length,
          highPriorityIssues: crashLoopDetails.length + failedDetails.length,
          mediumPriorityIssues: pendingDetails.length
        }
      };
    } catch (error) {
      console.error('❌ Failed to get pod health details:', error);
      throw error;
    }
  }

  async getResourceMetrics(): Promise<any> {
    try {      // Get top resource consumers with detailed breakdown
      const [topCPUPods, topMemoryPods, topCPUByNamespace, topMemoryByNamespace, nodeResourceUsage] = await Promise.all([
        this.queryInstant('topk(20, rate(container_cpu_usage_seconds_total{env="prod",image!="",name!=""}[5m]) * 1000)'),
        this.queryInstant('topk(20, container_memory_working_set_bytes{env="prod",image!="",name!=""} / 1024 / 1024)'),
        this.queryInstant('topk(10, sum by (namespace,cluster) (rate(container_cpu_usage_seconds_total{env="prod",image!=""}[5m])) * 1000)'),
        this.queryInstant('topk(10, sum by (namespace,cluster) (container_memory_working_set_bytes{env="prod",image!=""}) / 1024 / 1024)'),
        this.queryInstant('(1 - (node_memory_MemAvailable_bytes{env="prod"} / node_memory_MemTotal_bytes{env="prod"})) * 100')
      ]);

      // Process top CPU consuming pods
      const topCPUDetails = topCPUPods.data.result.map((pod: any, index: number) => ({
        rank: index + 1,
        podName: pod.metric.pod || pod.metric.container,
        namespace: pod.metric.namespace,
        cluster: pod.metric.cluster || 'unknown',
        node: pod.metric.node,
        cpuUsage: parseFloat(pod.value[1]).toFixed(2) + ' cores',
        severity: index < 5 ? 'high' : index < 10 ? 'medium' : 'low',
        action: index < 3 ? 'Immediate investigation required' : 'Monitor and optimize',
        timestamp: pod.value[0]
      }));

      // Process top Memory consuming pods
      const topMemoryDetails = topMemoryPods.data.result.map((pod: any, index: number) => ({
        rank: index + 1,
        podName: pod.metric.pod || pod.metric.container,
        namespace: pod.metric.namespace,
        cluster: pod.metric.cluster || 'unknown',
        node: pod.metric.node,
        memoryUsage: parseFloat(pod.value[1]).toFixed(0) + ' MB',
        severity: index < 5 ? 'high' : index < 10 ? 'medium' : 'low',
        action: index < 3 ? 'Check for memory leaks' : 'Monitor usage patterns',
        timestamp: pod.value[0]
      }));      // Process top CPU consuming namespaces
      const topCPUNamespaceDetails = topCPUByNamespace.data.result.map((ns: any, index: number) => ({
        rank: index + 1,
        namespace: ns.metric.namespace,
        cluster: ns.metric.cluster || 'unknown',
        cpuUsage: parseFloat(ns.value[1]).toFixed(2) + ' cores',
        recommendation: index < 3 ? 'Review resource quotas and limits' : 'Consider optimization',
        timestamp: ns.value[0]
      }));      // Process top Memory consuming namespaces
      const topMemoryNamespaceDetails = topMemoryByNamespace.data.result.map((ns: any, index: number) => ({
        rank: index + 1,
        namespace: ns.metric.namespace,
        cluster: ns.metric.cluster || 'unknown',
        memoryUsage: parseFloat(ns.value[1]).toFixed(0) + ' MB',
        recommendation: index < 3 ? 'Review memory allocation strategy' : 'Monitor growth trends',
        timestamp: ns.value[0]
      }));

      // Process node resource usage details
      const nodeResourceDetails = nodeResourceUsage.data.result.map((node: any) => ({
        nodeName: node.metric.node,
        cluster: node.metric.cluster || 'unknown',
        memoryUsagePercent: parseFloat(node.value[1]).toFixed(1),
        severity: parseFloat(node.value[1]) > 90 ? 'critical' : 
                 parseFloat(node.value[1]) > 80 ? 'high' : 
                 parseFloat(node.value[1]) > 70 ? 'medium' : 'normal',
        action: parseFloat(node.value[1]) > 90 ? 'Immediate action required - add capacity' : 
                parseFloat(node.value[1]) > 80 ? 'Plan capacity expansion' : 'Monitor',
        timestamp: node.value[0]
      }));

      return {
        topCPUPods: topCPUDetails,
        topMemoryPods: topMemoryDetails,
        topCPUNamespaces: topCPUNamespaceDetails,
        topMemoryNamespaces: topMemoryNamespaceDetails,
        nodeResourceDetails: nodeResourceDetails,
        alerts: [
          ...topCPUDetails.filter(p => p.severity === 'high').map(p => ({
            type: 'high_cpu_usage',
            message: `High CPU usage: ${p.podName} in ${p.namespace}`,
            severity: 'warning',
            cluster: p.cluster,
            namespace: p.namespace,
            pod: p.podName,
            action: p.action
          })),
          ...topMemoryDetails.filter(p => p.severity === 'high').map(p => ({
            type: 'high_memory_usage',
            message: `High memory usage: ${p.podName} in ${p.namespace}`,
            severity: 'warning',
            cluster: p.cluster,
            namespace: p.namespace,
            pod: p.podName,
            action: p.action
          })),
          ...nodeResourceDetails.filter(n => n.severity === 'critical' || n.severity === 'high').map(n => ({
            type: 'node_resource_pressure',
            message: `Node ${n.nodeName} memory usage: ${n.memoryUsagePercent}%`,
            severity: n.severity === 'critical' ? 'error' : 'warning',
            cluster: n.cluster,
            node: n.nodeName,
            action: n.action
          }))
        ]
      };
    } catch (error) {
      console.error('❌ Failed to get resource metrics details:', error);
      throw error;
    }
  }

  async getNetworkMetrics(): Promise<any> {
    try {
      const [throughputRx, throughputTx, errors] = await Promise.all([
        this.queryInstant('sum(rate(container_network_receive_bytes_total{env="prod"}[5m]))'),
        this.queryInstant('sum(rate(container_network_transmit_bytes_total{env="prod"}[5m]))'),
        this.queryInstant('sum(rate(container_network_receive_errors_total{env="prod"}[5m]) + rate(container_network_transmit_errors_total{env="prod"}[5m]))')
      ]);

      return {
        throughputRx: parseFloat(throughputRx.data.result[0]?.value?.[1] || '0'),
        throughputTx: parseFloat(throughputTx.data.result[0]?.value?.[1] || '0'),
        errors: parseFloat(errors.data.result[0]?.value?.[1] || '0')
      };
    } catch (error) {
      console.error('❌ Failed to get network metrics:', error);
      throw error;
    }
  }

  async getSchedulingIssues(): Promise<any> {
    try {
      // Get detailed scheduling problems and capacity constraints
      // TODO: Implement actual logic or remove stub
      return {};
    } catch (error) {
      console.error('❌ Failed to get scheduling issues:', error);
      throw error;
    }
  }

  // Helper for AKS Disk Usage fetch
  async getAksDiskUsage(): Promise<any[]> {
    try {
      // Use integrated Flask app oracle proxy endpoint
      const response = await this.axiosInstance.get(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/v1/aks_disk_usage`);
      // Prometheus result: response.data.data.result
      return response.data.data.result || [];
    } catch (error: any) {
      console.error('❌ Failed to fetch AKS disk usage:', error);
      throw new Error(error.message || 'Failed to fetch AKS disk usage');
    }
  }

  // Leading Indicator Metric Helpers
  async getClusterNodesReady(): Promise<PrometheusResponse> {
    const query = '(sum(kube_node_status_condition{condition="Ready",status="true",env="prod",k8s_cluster!=""}) / sum(kube_node_status_condition{condition="Ready",env="prod",k8s_cluster!=""})) * 100';
    return await this.queryInstant(query);
  }

  async getPodsReady(): Promise<PrometheusResponse> {
    const query = '(sum(kube_pod_status_ready{condition="true",env="prod",k8s_cluster!=""}) / sum(kube_pod_status_ready{env="prod",k8s_cluster!=""})) * 100';
    return await this.queryInstant(query);
  }

  async getOOMKilledContainers(limit: number = 15): Promise<PrometheusResponse> {
    // If limit is very high (1000+), don't use topk to get ALL results
    const query = limit >= 1000 
      ? `sum by (k8s_cluster,namespace,pod) (kube_pod_container_status_terminated_reason{reason="OOMKilled",env="prod",k8s_cluster!=""})`
      : `topk(${limit}, sum by (k8s_cluster,namespace,pod) (kube_pod_container_status_terminated_reason{reason="OOMKilled",env="prod",k8s_cluster!=""}))`;
    return await this.queryInstant(query);
  }
  async getContainerMemoryUtilisation(limit: number = 15): Promise<PrometheusResponse> {
    // Only show containers with memory utilization >= 90%
    const query = limit >= 1000
      ? `(100 * sum by (k8s_cluster,namespace,pod,container) (container_memory_working_set_bytes{image!="",env="prod",k8s_cluster!=""}) / sum by (k8s_cluster,namespace,pod,container) (kube_pod_container_resource_limits{resource="memory",env="prod",k8s_cluster!=""})) >= 90`
      : `topk(${limit}, (100 * sum by (k8s_cluster,namespace,pod,container) (container_memory_working_set_bytes{image!="",env="prod",k8s_cluster!=""}) / sum by (k8s_cluster,namespace,pod,container) (kube_pod_container_resource_limits{resource="memory",env="prod",k8s_cluster!=""})) >= 90)`;
    return await this.queryInstant(query);
  }

  async getContainerCPUUtilisation(limit: number = 15): Promise<PrometheusResponse> {
    // Only show containers with CPU utilization >= 90%
    const query = limit >= 1000
      ? `(100 * sum by (k8s_cluster,namespace,pod,container) (rate(container_cpu_usage_seconds_total{image!="",env="prod",k8s_cluster!=""}[5m])) / sum by (k8s_cluster,namespace,pod,container) (kube_pod_container_resource_limits{resource="cpu",env="prod",k8s_cluster!=""})) >= 90`
      : `topk(${limit}, (100 * sum by (k8s_cluster,namespace,pod,container) (rate(container_cpu_usage_seconds_total{image!="",env="prod",k8s_cluster!=""}[5m])) / sum by (k8s_cluster,namespace,pod,container) (kube_pod_container_resource_limits{resource="cpu",env="prod",k8s_cluster!=""})) >= 90)`;
    return await this.queryInstant(query);
  }

  async getNodeMemoryUsage(limit: number = 15): Promise<PrometheusResponse> {
    // Only show nodes with memory usage >= 90%
    const query = limit >= 1000
      ? `((1 - (node_memory_MemAvailable_bytes{env="prod",k8s_cluster!=""} / node_memory_MemTotal_bytes{env="prod",k8s_cluster!=""})) * 100) >= 90`
      : `topk(${limit}, ((1 - (node_memory_MemAvailable_bytes{env="prod",k8s_cluster!=""} / node_memory_MemTotal_bytes{env="prod",k8s_cluster!=""})) * 100) >= 90)`;
    return await this.queryInstant(query);
  }

  async getNodeCPUUsage(): Promise<PrometheusResponse> {
    // Only show nodes with CPU usage >= 90%
    const query = 'topk(15, ((1 - avg(rate(node_cpu_seconds_total{mode="idle",env="prod",k8s_cluster!=""}[5m])) by (node_name,k8s_cluster)) * 100) >= 90)';
    return await this.queryInstant(query);
  }

  async getCrashLoopBackOffPods(limit: number = 15): Promise<PrometheusResponse> {
    const query = limit >= 1000
      ? `sum by (k8s_cluster,namespace,pod) (kube_pod_container_status_waiting_reason{reason="CrashLoopBackOff",env="prod",k8s_cluster!=""})`
      : `topk(${limit}, sum by (k8s_cluster,namespace,pod) (kube_pod_container_status_waiting_reason{reason="CrashLoopBackOff",env="prod",k8s_cluster!=""}))`;
    return await this.queryInstant(query);
  }
  async getPodRestartIncrease(limit: number = 15): Promise<PrometheusResponse> {
    // Only show pods with > 5 restarts in the last hour to indicate actual issues
    const query = limit >= 1000
      ? `(increase(kube_pod_container_status_restarts_total{env="prod",k8s_cluster!=""}[1h]) > 5)`
      : `topk(${limit}, (increase(kube_pod_container_status_restarts_total{env="prod",k8s_cluster!=""}[1h]) > 5))`;
    return await this.queryInstant(query);
  }
  async getPVCUsage(limit: number = 15): Promise<PrometheusResponse> {
    // Only show PVCs with usage >= 90% to avoid showing all PVCs
    const query = limit >= 1000
      ? `(100 * kubelet_volume_stats_used_bytes{env="prod",k8s_cluster!=""} / kubelet_volume_stats_capacity_bytes{env="prod",k8s_cluster!=""}) >= 90`
      : `topk(${limit}, (100 * kubelet_volume_stats_used_bytes{env="prod",k8s_cluster!=""} / kubelet_volume_stats_capacity_bytes{env="prod",k8s_cluster!=""}) >= 90)`;
    return await this.queryInstant(query);
  }

  async getDiskPressureNodes(): Promise<PrometheusResponse> {
    // Only show nodes that actually have disk pressure (status="true" means there IS disk pressure)
    const query = 'kube_node_status_condition{condition="DiskPressure",status="true",env="prod",k8s_cluster!=""} > 0';
    return await this.queryInstant(query);
  }

  async getPodIOPS(limit: number = 15): Promise<PrometheusResponse> {
    // Only show pods with I/O utilization > 85% to filter out idle pods
    const query = limit >= 1000
      ? `(rate(container_fs_io_time_seconds_total{env="prod",k8s_cluster!="",metrics_path!=""}[5m]) * 100) > 85`
      : `topk(${limit}, (rate(container_fs_io_time_seconds_total{env="prod",k8s_cluster!="",metrics_path!=""}[5m]) * 100) > 85)`;
    return await this.queryInstant(query);
  }

  async getNetworkErrors(limit: number = 15): Promise<PrometheusResponse> {
    // Only show pods with network errors > 0 to avoid showing all pods
    const query = limit >= 1000
      ? `(rate(container_network_receive_errors_total{env="prod",k8s_cluster!="",metrics_path!=""}[5m]) + rate(container_network_transmit_errors_total{env="prod",k8s_cluster!="",metrics_path!=""}[5m])) > 0`
      : `topk(${limit}, (rate(container_network_receive_errors_total{env="prod",k8s_cluster!="",metrics_path!=""}[5m]) + rate(container_network_transmit_errors_total{env="prod",k8s_cluster!="",metrics_path!=""}[5m])) > 0)`;
    return await this.queryInstant(query);
  }
  async getAPIServerLatency(limit: number = 15): Promise<PrometheusResponse> {
    // Only show API server latencies > 1 second (1000ms) to indicate actual performance issues
    const query = limit >= 1000
      ? `(histogram_quantile(0.95, sum(rate(apiserver_request_duration_seconds_bucket{env="prod",k8s_cluster!="",app!=""}[5m])) by (le, verb, k8s_cluster)) > 1)`
      : `topk(${limit}, (histogram_quantile(0.95, sum(rate(apiserver_request_duration_seconds_bucket{env="prod",k8s_cluster!="",app!=""}[5m])) by (le, verb, k8s_cluster)) > 1))`;
    return await this.queryInstant(query);
  }

  /**
   * Given an anomaly (e.g., high CPU/memory pod), filter and query correlated metrics.
   * @param anomaly {object} - { type, podName, namespace, cluster, node }
   * @returns {Promise<object>} - Correlated metrics for the anomaly
   */
  async getCorrelatedMetricsForAnomaly(anomaly: {
    type: string;
    podName?: string;
    namespace?: string;
    cluster?: string;
    node?: string;
  }): Promise<any> {
    // Select relevant metrics for correlation
    const relevantMetrics = [
      // Always correlate with restarts, OOMKilled, CrashLoopBackOff, node pressure, PVC usage, network errors, API server latency
      { name: 'kube_pod_container_status_restarts_total', query: `increase(kube_pod_container_status_restarts_total{pod="${anomaly.podName}",namespace="${anomaly.namespace}",env="prod"}[1h])` },
      { name: 'kube_pod_container_status_terminated_reason', query: `kube_pod_container_status_terminated_reason{reason="OOMKilled",pod="${anomaly.podName}",namespace="${anomaly.namespace}",env="prod"}` },
      { name: 'kube_pod_container_status_waiting_reason', query: `kube_pod_container_status_waiting_reason{reason="CrashLoopBackOff",pod="${anomaly.podName}",namespace="${anomaly.namespace}",env="prod"}` },
      { name: 'kube_node_status_condition', query: `kube_node_status_condition{condition="DiskPressure",status="true",node="${anomaly.node}",env="prod"}` },
      { name: 'container_memory_working_set_bytes', query: `container_memory_working_set_bytes{pod="${anomaly.podName}",namespace="${anomaly.namespace}",env="prod"}` },
      { name: 'container_cpu_usage_seconds_total', query: `rate(container_cpu_usage_seconds_total{pod="${anomaly.podName}",namespace="${anomaly.namespace}",env="prod"}[5m])` },
      { name: 'kubelet_volume_stats_used_bytes', query: `kubelet_volume_stats_used_bytes{pod="${anomaly.podName}",namespace="${anomaly.namespace}",env="prod"}` },
      { name: 'container_fs_reads_total', query: `rate(container_fs_reads_total{pod="${anomaly.podName}",namespace="${anomaly.namespace}",env="prod"}[5m])` },
      { name: 'container_fs_writes_total', query: `rate(container_fs_writes_total{pod="${anomaly.podName}",namespace="${anomaly.namespace}",env="prod"}[5m])` },
      { name: 'container_network_receive_errors_total', query: `rate(container_network_receive_errors_total{pod="${anomaly.podName}",namespace="${anomaly.namespace}",env="prod"}[5m])` },
      { name: 'container_network_transmit_errors_total', query: `rate(container_network_transmit_errors_total{pod="${anomaly.podName}",namespace="${anomaly.namespace}",env="prod"}[5m])` },
      { name: 'apiserver_request_duration_seconds_bucket', query: `histogram_quantile(0.95, sum(rate(apiserver_request_duration_seconds_bucket{namespace="${anomaly.namespace}",env="prod"}[5m])) by (le, verb))` },
    ];
    // Query all relevant metrics in parallel
    const results: any = {};
    await Promise.all(relevantMetrics.map(async metric => {
      try {
        results[metric.name] = await this.queryInstant(metric.query);
      } catch (err) {
        results[metric.name] = { error: (err as any).message };
      }
    }));
    return results;
  }
  /**
   * Send anomaly and correlated metrics to AI for recommendations
   * @param anomaly {object}
   * @param correlatedMetrics {object} - Additional frontend-fetched metrics for context
   * @returns {Promise<object>} - AI recommendations
   */
  async getAIRecommendationsForAnomaly(anomaly: any, correlatedMetrics: any): Promise<any> {
    try {
      console.log('📤 Sending AI recommendation request:', { anomaly, correlatedMetrics });
      const response = await this.axiosInstance.post(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/v1/ai_recommendations_enhanced`, {
        anomaly,
        correlatedMetrics
      });
      console.log('📥 AI recommendation response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to get AI recommendations:', error);
      return { error: error.message || 'Failed to get AI recommendations' };
    }
  }

  /**
   * Get pod logs from Loki via the backend test_loki_logs endpoint
   * @param cluster {string} - Cluster name (e.g., "prod", "staging")
   * @param podName {string} - Name of the pod
   * @param namespace {string} - Namespace of the pod (optional)
   * @param tailLines {number} - Number of log lines to fetch (default: 50)
   * @returns {Promise<string[]>} - Array of log lines
   */
  async getPodLogs(cluster: string, podName: string, namespace?: string, tailLines: number = 50): Promise<string[]> {
    try {
      // Validate parameters
      if (!cluster || !podName) {
        throw new Error('Cluster and podName are required parameters');
      }

      // Log the parameters for debugging
      console.log('📤 Fetching pod logs from Loki:', { cluster, podName, namespace, tailLines });

      const params = new URLSearchParams({
        cluster: cluster,
        pod: podName
      });

      const response = await this.axiosInstance.get(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/v1/test_loki_logs?${params}`);
      console.log('📥 Pod logs response received from Loki');

      // Backend returns {"logs": [...]}
      return response.data?.logs || [];
    } catch (error: any) {
      // Handle 404 (endpoint not implemented) gracefully
      if (error.response?.status === 404) {
        console.log('ℹ️ Loki logs endpoint not implemented, proceeding with metrics-only analysis');
        return ['Pod logs endpoint not available. Analysis will proceed with available metrics and status information.'];
      }

      // Log the full error for debugging
      console.error('❌ Error fetching pod logs:', {
        error: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        params: { cluster, podName, namespace }
      });

      // Return a helpful message instead of throwing
      return [`Pod logs not available: ${error.message}. The AI analysis will proceed with available metrics.`];
    }
  }

  // Linux compute metrics from Grafana JSON
  async getLinuxServerStatus() {
    const query = `up{env="prod",provider=~".*", os=~"Linux|linux"} == 0`;
    return this.queryInstant(query);
  }

  async getLinuxCPUUsage() {
    const query = `100 - (avg by (node_name,product_id,app) (irate(node_cpu_seconds_total{env="prod",provider=~".*",mode="idle",node_name!~"aks.*"}[2m])) * 100) >= 90`;
    return this.queryInstant(query);
  }

  async getLinuxMemoryUsage() {
    const query = `(100 - 100 * node_memory_MemAvailable_bytes{provider=~".*", env="prod",node_name!~"aks.*"} / node_memory_MemTotal_bytes{provider=~".*", env="prod",node_name!~"aks.*"}) >= 90`;
    return this.queryInstant(query);
  }

  async getLinuxDiskUsage() {
    const query = `(1 - node_filesystem_avail_bytes{env="prod",provider=~".*",mountpoint=~"/data|/home|/var|/usr|/mnt",device!~'rootfs'} / node_filesystem_size_bytes{env="prod",provider=~".*",mountpoint=~"/data|/home|/var|/usr|/mnt",device!~'rootfs'})*100 > 90`;
    return this.queryInstant(query);
  }

  async getLinuxDiskIO() {
    const query = `avg by (node_name,instance, product_id, app) (rate(node_disk_io_time_seconds_total{env="prod",provider=~".*",product_id!="perpetual-platform",node_name!~"aks.*"}[2m])) *100 > 85`;
    return this.queryInstant(query);
  }

  async getLinuxNetworkReceive() {
    const query = `sum by(instance,device,node_name,product_id)(irate(node_network_receive_bytes_total{env="prod",provider=~".*",product_id!="perpetual-platform",node_name!~"aks.*"}[5m])*8) > 100000000`;
    return this.queryInstant(query);
  }

  async getLinuxNetworkTransmit() {
    const query = `sum by(instance,device,node_name,product_id)(irate(node_network_transmit_bytes_total{env="prod",provider=~".*",product_id!="perpetual-platform",node_name!~"aks.*"}[5m])*8) > 100000000`;
    return this.queryInstant(query);
  }

  async getLinuxNetworkDrops() {
    const query = `sum by(device,node_name,instance)(irate(node_network_receive_drop_total{env="prod",node_name!~"aks.*"}[5m])*8) > 0`;
    return this.queryInstant(query);
  }

  // AIX compute metrics from Grafana JSON
  // async getAIXServerStatus() {
  //   const query = `up{env="prod",provider="onprem"} <= 0`;
  //   return this.queryInstant(query);
  // }

  async getAIXCPUUsage() {
    const query = `sum by(node_name) (aix_cpu_used_percent{provider="onprem",env="prod",mode!="idle"}) >= 90`;
    return this.queryInstant(query);
  }

  async getAIXMemoryUsage() {
    const query = `aix_mem_used_percent{provider="onprem",env="prod"} >= 90`;
    return this.queryInstant(query);
  }

  async getAIXDiskUsage() {
    const query = `aix_disk_used_percent{env="prod"} > 85`;
    return this.queryInstant(query);
  }

  async getAIXDiskInodeUsage() {
    const query = `aix_disk_inode_used_percent{env="prod"} > 85`;
    return this.queryInstant(query);
  }

  // Comprehensive Linux metrics fetch for cache
  async fetchLinuxMetrics() {
    const metrics = await Promise.allSettled([
      this.getLinuxCPUUsage(),
      this.getLinuxMemoryUsage(),
      this.getLinuxDiskUsage(),
      this.getLinuxDiskIO(),
      this.getLinuxNetworkReceive(),
      this.getLinuxNetworkTransmit(),
      this.getLinuxNetworkDrops()
    ]);

    const results: any[] = [];
    
    metrics.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value?.data?.result) {
        const metricNames = [
          'CPU Usage',
          'Memory Usage', 
          'Disk Usage',
          'Disk IO',
          'Network Receive',
          'Network Transmit',
          'Network Drops'
        ];
        
        result.value.data.result.forEach((item: any) => {
          let value = parseFloat(item.value[1] || '0');
          let severity: 'critical' | 'warning' | 'healthy' = 'healthy';
          
          // Assign severity based on metric type and thresholds
          switch (index) {
            case 0: // CPU Usage
            case 1: // Memory Usage
            case 2: // Disk Usage
            case 3: // Disk IO
              severity = value >= 95 ? 'critical' : value >= 90 ? 'warning' : 'healthy';
              break;
            case 4: // Network Receive
            case 5: // Network Transmit
              severity = value >= 100000000 ? 'warning' : 'healthy';
              break;
            case 6: // Network Drops
              severity = value > 1000 ? 'critical' : value > 0 ? 'warning' : 'healthy';
              break;
          }
          
          // Include ALL metrics (healthy, warning, critical) for proper health calculation
          results.push({
            name: metricNames[index],
            value,
            severity,
            description: `${item.metric.node_name || item.metric.instance} - ${metricNames[index]}: ${value}${item.metric.unit || ''}`,
            node_name: item.metric.node_name || item.metric.instance,
            product_id: item.metric.product_id || '-',
            app: item.metric.app || '-'
          });
        });
      }
    });

    return results;
  }

  // Comprehensive AIX metrics fetch for cache
  async fetchAIXMetrics() {
    const metrics = await Promise.allSettled([
      // this.getAIXServerStatus(),
      this.getAIXCPUUsage(),
      this.getAIXMemoryUsage(),
      this.getAIXDiskUsage(),
      this.getAIXDiskInodeUsage()
    ]);

    const results: any[] = [];
    
    metrics.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value?.data?.result) {
        const metricNames = [
          // 'Server Status',
          'CPU Usage',
          'Memory Usage', 
          'Disk Usage',
          'Disk Inode Usage'
        ];
        
        result.value.data.result.forEach((item: any) => {
          let value = parseFloat(item.value[1] || '0');
          let severity: 'critical' | 'warning' | 'healthy' = 'healthy';
          
          // Assign severity based on metric type and thresholds
          switch (index) {
            // case 0: // Server Status
            //   severity = value === 0 ? 'critical' : 'healthy';
            //   break;
            case 0: // CPU Usage
            case 1: // Memory Usage
            case 2: // Disk Usage
            case 3: // Disk Inode Usage
              severity = value >= 95 ? 'critical' : value >= 90 ? 'warning' : 'healthy';
              break;
          }
          
          // Include ALL metrics (healthy, warning, critical) for proper health calculation
          results.push({
            name: metricNames[index],
            value,
            severity,
            description: `${item.metric.node_name || item.metric.instance} - ${metricNames[index]}: ${value}${item.metric.unit || ''}`,
            node_name: item.metric.node_name || item.metric.instance,
            product_id: item.metric.product_id || '-',
            app: item.metric.app || '-'
          });
        });
      }
    });

    return results;
  }

  // Windows Server Status - Services not running
  async getWindowsServiceStatus() {
    return this.queryInstant(`windows_service_state{env="prod",state!="running"}`);
  }

  // Windows Network Errors - Discarded/Error packets
  async getWindowsNetworkErrors() {
    return this.queryInstant(`(irate(windows_net_packets_outbound_discarded_total{env="prod"}[5m]) + irate(windows_net_packets_outbound_errors_total{env="prod"}[5m]) + irate(windows_net_packets_received_discarded_total{env="prod"}[5m]) + irate(windows_net_packets_received_errors_total{env="prod"}[5m])) > 10`);
  }

  // Windows System Exceptions
  async getWindowsSystemExceptions() {
    return this.queryInstant(`irate(windows_system_exception_dispatches_total{env="prod"}[5m]) > 10`);
  }

  // Windows CPU Usage
  async getWindowsCPUUsage() {
    return this.queryInstant(`100 - (avg(irate(windows_cpu_time_total{env="prod",mode="idle"}[2m])))*100 >= 90`);
  }

  // Windows Memory Usage
  async getWindowsMemoryUsage() {
    return this.queryInstant(`100 - (windows_os_physical_memory_free_bytes{env="prod"} / windows_cs_physical_memory_bytes{env="prod"})*100 >= 90`);
  }

  // Windows Disk Usage
  async getWindowsDiskUsage() {
    return this.queryInstant(`100 - (windows_logical_disk_free_bytes{env="prod", volume=~".:"} / windows_logical_disk_size_bytes{env=~"prod", volume=~".:"}) * 100 >= 90`);
  }

  // Windows Disk IO
  async getWindowsDiskIO() {
    return this.queryInstant(`irate(windows_logical_disk_read_bytes_total{env="prod", volume=~".:"}[5m]) + irate(windows_logical_disk_write_bytes_total{env="prod", volume=~".:"}[5m]) > 5000000`);
  }

  // Comprehensive Windows metrics fetch for cache
  async fetchWindowsMetrics() {
    const metrics = await Promise.allSettled([
      this.getWindowsServiceStatus(),
      this.getWindowsNetworkErrors(),
      this.getWindowsSystemExceptions(),
      this.getWindowsCPUUsage(),
      this.getWindowsMemoryUsage(),
      this.getWindowsDiskUsage(),
      this.getWindowsDiskIO()
    ]);

    const results: any[] = [];
    
    metrics.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value?.data?.result) {
        const metricNames = [
          'Service Status',
          'Network Errors',
          'System Exceptions',
          'CPU Usage',
          'Memory Usage',
          'Disk Usage',
          'Disk IO'
        ];
        
        result.value.data.result.forEach((item: any) => {
          let value = parseFloat(item.value[1] || '0');
          let severity: 'critical' | 'warning' | 'healthy' = 'healthy';
          
          // Assign severity based on metric type and thresholds
          switch (index) {
            case 0: // Service Status - any non-running service is critical
            case 1: // Network Errors - any errors are critical
            case 2: // System Exceptions - any exceptions are critical
              severity = value > 0 ? 'critical' : 'healthy';
              break;
            case 3: // CPU Usage
            case 4: // Memory Usage
            case 5: // Disk Usage
              severity = value >= 95 ? 'critical' : value >= 90 ? 'warning' : 'healthy';
              break;
            case 6: // Disk IO - high I/O is warning
              severity = value > 10000000 ? 'critical' : value > 5000000 ? 'warning' : 'healthy';
              break;
          }
          
          // Include ALL metrics (healthy, warning, critical) for proper health calculation
          results.push({
            name: metricNames[index],
            value,
            severity,
            description: `${item.metric.node_name || item.metric.instance} - ${metricNames[index]}: ${value}${item.metric.unit || ''}`,
            node_name: item.metric.node_name || item.metric.instance,
            product_id: item.metric.product_id || '-',
            app: item.metric.app || '-'
          });
        });
      }
    });

    return results;
  }

  async getServerAnalysis(provider: string, filters: {
    product_id?: string;
    environment?: string;
    hostname?: string;
    cluster?: string;
    namespace?: string;
    time_range?: string;
  }): Promise<{
    data: {
      capacitySummary: {
        avgCpuUsage?: string;
        avgMemory?: string;
        totalCores?: number;
        totalRam?: string;
        operatingSystem?: string;
        serverType?: string;
        cpuInfo?: string;
        // New fields for requests data
        avgCpuRequests?: string;
        avgMemoryRequests?: string;
        totalCpuRequests?: string;
        totalMemoryRequests?: string;
      };
      performanceMetrics: Array<{
        name: string;
        mean: string;
        maximum: string;
        minimum: string;
        status: 'Low' | 'Medium' | 'High';
        volumes?: Array<{
          capacity_gb: number;
          free_gb: number;
          status: string;
          type: string;
          usage_percent: number;
          used_gb: number;
          volume: string;
        }>;
      }>;
      usageTrends: Array<{
        title: string;
        trend: 'Stable' | 'Increasing' | 'Decreasing' | 'Moderate' | 'No_data';
        description: string;
        color: 'primary' | 'secondary' | 'success' | 'warning' | 'info';
        volume_details?: Array<{
          capacity_gb: number;
          current_usage: number;
          free_gb: number;
          trend_points: number;
          type: string;
          volume: string;
        }>;
      }>;
      recommendations: Array<{
        type: string;
        priority?: string;
        description: string;
        estimated_savings?: number;
        // New fields from server analysis recommendations
        analysis_type?: string;
        message?: string;
        affected_containers?: string[];
        potential_savings?: string;
      }>;
      // New field for SysOps Operations
      sysOpsOperations?: Array<{
        operation: string;
        status: 'success' | 'warning' | 'error' | 'info';
        description: string;
        timestamp: string;
        details?: any;
      }>;
      // New field for SysOps Summary
      sysops_summary?: {
        title: string;
        description: string;
        key_metrics: {
          cpu_limits_avg?: string;
          memory_limits_avg?: string;
          oom_containers?: number;
          throttling_containers?: number;
          [key: string]: any;
        };
      };
    };
    infrastructure_type: string;
    success: boolean;
    time_range: string;
    timestamp: string;
  }> {
    const params = new URLSearchParams();
    if (filters.product_id) params.append('product_id', filters.product_id);
    if (filters.environment) params.append('environment', filters.environment);
    if (filters.hostname) params.append('hostname', filters.hostname);
    if (filters.cluster) params.append('cluster', filters.cluster);
    if (filters.namespace) params.append('namespace', filters.namespace);
    if (filters.time_range) params.append('time_range', filters.time_range);

    const url = `${API_BASE_URL}/api/sysops-capacity/server-analysis/${provider}?${params.toString()}`;
    console.log('🌐 API Call URL:', url);
    console.log('🌐 Query parameters:', Object.fromEntries(params.entries()));

    const response = await this.axiosInstance.get(url);
    return response.data;
  }

  async getAIRecommendations(provider: string, filters: {
    product_id?: string;
    environment?: string;
    hostname?: string;
    cluster?: string;
    namespace?: string;
    time_range?: string;
  }): Promise<{
    success: boolean;
    data: Array<{
      type: string;
      priority: 'low' | 'medium' | 'high' | 'critical';
      title: string;
      description: string;
      actions: string[];
      estimated_impact: string;
      timeframe: string;
    }>;
    infrastructure_type: string;
    time_range: string;
    timestamp: string;
  }> {
    const params = new URLSearchParams();
    if (filters.product_id) params.append('product_id', filters.product_id);
    if (filters.environment) params.append('environment', filters.environment);
    if (filters.hostname) params.append('hostname', filters.hostname);
    if (filters.cluster) params.append('cluster', filters.cluster);
    if (filters.namespace) params.append('namespace', filters.namespace);
    if (filters.time_range) params.append('time_range', filters.time_range);

    const url = `${API_BASE_URL}/api/sysops-capacity/ai-recommendations/${provider}?${params.toString()}`;
    console.log('🤖 AI Recommendations API Call URL:', url);
    console.log('🤖 Query parameters:', Object.fromEntries(params.entries()));

    const response = await this.axiosInstance.get(url);
    return response.data;
  }

  // New unified API to load all server infrastructure data at once
  async getAllServerInfo(filterType?: 'dbops' | null): Promise<{
    status: string;
    data: {
      platforms: {
        windows: {
          servers: Array<{
            product_id: string;
            hostname: string;
            product_name?: string;
            os_version?: string;
            last_seen: string;
            env?: string;
          }>;
          product_ids: string[];
          server_count: number;
          product_id_count: number;
        };
        linux: {
          servers: Array<{
            product_id: string;
            hostname: string;
            product_name?: string;
            os_version?: string;
            last_seen: string;
            env?: string;
          }>;
          product_ids: string[];
          server_count: number;
          product_id_count: number;
        };
        aks: {
          servers: Array<{
            product_id: string;
            pod: string;
            cluster: string;
            namespace: string;
            product_name?: string;
            last_seen: string;
            env?: string;
          }>;
          product_ids: string[];
          server_count: number;
          product_id_count: number;
          hierarchy: {
            clusters: Array<{
              cluster: string;
              namespaces: Array<{
                name: string;
                phase: string;
              }>;
              namespace_count: number;
            }>;
            total_clusters: number;
            total_namespaces: number;
          };
        };
      };
      summary: {
        total_servers: number;
        total_product_ids: number;
        total_clusters: number;
        total_namespaces: number;
        last_updated: string;
      };
    };
  }> {
    try {
      // Build query parameter for filter if provided
      const queryParams = filterType ? `?filter_type=${filterType}` : '';
      const response = await this.axiosInstance.get(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/v1/server-info/all${queryParams}`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to load all server info:', error);
      throw new Error(`Failed to load server data: ${error.response?.data?.error || error.message}`);
    }
  }

  async getMSSQLServerInfo(): Promise<{
    status: string;
    data: {
      servers: Array<{
        node_name: string;
        product_id: string;
        env: string;
        hosted: string;
        mssql_instance?: string;
        sql_version?: string;
        provider?: string;
        region?: string;
        server_type?: string;
      }>;
      product_ids: string[];
      summary: {
        total_servers: number;
        total_product_ids: number;
        sql_versions?: { [key: string]: number };
        hosting_types?: { [key: string]: number };
        last_updated: string;
      };
    };
    cache_info: {
      cache_age_seconds: number;
      is_fresh: boolean;
      response_time_seconds: number;
      cache_duration_seconds: number;
    };
  }> {
    try {
      const response = await this.axiosInstance.get(`${this.apiConfig.metricsBaseUrl}/api/oracle-proxy/v1/mssql-server-info/all`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to load MSSQL server info:', error);
      throw new Error(`Failed to load MSSQL server data: ${error.response?.data?.error || error.message}`);
    }
  }

  // DBOps Methods
  async getDBOpsCPUThresholdAnalysis(
    productId: string,
    environment: string,
    vmName: string,
    timeRange: string
  ): Promise<any> {
    try {
      const response = await this.axiosInstance.get(
        `${this.apiConfig.metricsBaseUrl}/api/dbops/cpu-threshold-analysis/${productId}/${environment}/${vmName}`,
        {
          params: { time_range: timeRange }
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to get CPU threshold analysis:', error);
      throw new Error(`Failed to get CPU threshold analysis: ${error.response?.data?.error || error.message}`);
    }
  }

  async getDBOpsCPUThresholdAnalysisMulti(
    servers: Array<{ product_id: string; environment: string; vm_name: string; hosting_type?: string; region?: string }>,
    timeRange: string
  ): Promise<any> {
    try {
      const response = await this.axiosInstance.post(
        `${this.apiConfig.metricsBaseUrl}/api/dbops/cpu-threshold-analysis-multi`,
        {
          servers: servers,
          time_range: timeRange
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to get multi-VM CPU threshold analysis:', error);
      throw new Error(`Failed to get multi-VM CPU threshold analysis: ${error.response?.data?.error || error.message}`);
    }
  }

  async getDBOpsMemoryThresholdAnalysisMulti(
    servers: Array<{ product_id: string; environment: string; vm_name: string; hosting_type?: string; region?: string }>,
    timeRange: string
  ): Promise<any> {
    try {
      const response = await this.axiosInstance.post(
        `${this.apiConfig.metricsBaseUrl}/api/dbops/memory-threshold-analysis-multi`,
        {
          servers: servers,
          time_range: timeRange
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to get multi-VM memory threshold analysis:', error);
      throw new Error(`Failed to get multi-VM memory threshold analysis: ${error.response?.data?.error || error.message}`);
    }
  }

  async getDBOpsBufferHitRatioThresholdAnalysisMulti(
    servers: Array<{ product_id: string; environment: string; vm_name: string; hosting_type?: string; region?: string }>,
    timeRange: string
  ): Promise<any> {
    try {
      const response = await this.axiosInstance.post(
        `${this.apiConfig.metricsBaseUrl}/api/dbops/buffer-hit-ratio-threshold-analysis-multi`,
        {
          servers: servers,
          time_range: timeRange
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to get multi-VM buffer hit ratio threshold analysis:', error);
      throw new Error(`Failed to get multi-VM buffer hit ratio threshold analysis: ${error.response?.data?.error || error.message}`);
    }
  }

  async getDBOpsCapacityAnalysisMulti(
    servers: Array<{ product_id: string; environment: string; vm_name: string; hosting_type?: string; region?: string }>,
    timeRange: string,
    customStartDate?: string,
    customEndDate?: string
  ): Promise<any> {
    try {
      const requestBody: any = {
        servers: servers,
        time_range: timeRange
      };

      // Add custom dates if provided
      if (timeRange === 'custom' && customStartDate && customEndDate) {
        requestBody.custom_start_date = customStartDate;
        requestBody.custom_end_date = customEndDate;
      }

      const response = await this.axiosInstance.post(
        `${this.apiConfig.metricsBaseUrl}/api/dbops/capacity-analysis-multi`,
        requestBody
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to get comprehensive capacity analysis:', error);
      throw new Error(`Failed to get comprehensive capacity analysis: ${error.response?.data?.error || error.message}`);
    }
  }
}

export const apiClient = new MaerskApiClient();
export default apiClient;
